import { useState, useEffect, useMemo, useRef } from "react";
import { Form } from "antd";
import { getMsg } from "../../utils/antdStatic";
import api from "../../services/api";
import { apiFetch, type ErrorApi } from "../../services/apiClient";
import { useSidebar } from "../../hooks/useSidebar";
import { useFiltroPersistido } from "../../hooks/useFiltroPersistido";
import { normalize, matches } from "../../utils/string.utils";
import { formatPropertyName } from "../../utils/propertyDisplay";
import { telefonoParaEnviar } from "../../utils/telefonoE164";
import { irAlPrimerError } from "../../utils/formularioErrores";
import { marcarCamposTelefono } from "../../utils/telefonoErrorApi";

type TabKey = "cliente" | "propietario";

/** Los tres teléfonos del formulario de clientes y propietarios (cliente, propietario y «quien recibe el pago»). */
const CAMPOS_TELEFONO = ["telefonoUsuario", "telefonoPropietario", "telefonoQuienRecibe"] as const;

/**
 * Shape of a contact user as returned by /users/clientes-propietarios
 * (backend camelCase keys). Distinct from the global User in api.interfaces.ts.
 */
interface ContactUser {
  id: number;
  nombreUsuario?: string;
  emailUsuario?: string;
  telefonoUsuario?: string;
  nombrePropietario?: string;
  emailPropietario?: string;
  telefonoPropietario?: string;
  rutQuienRecibe?: string;
  bancoQuienRecibe?: string;
  tipocuentaQuienRecibe?: string;
  numerocuentaQuienRecibe?: string;
  nombreQuienRecibe?: string;
  emailQuienRecibe?: string;
  telefonoQuienRecibe?: string;
  password?: string;
  propiedades_maneja?: string[];
  propiedades_tiene?: string[];
  /** Avatar del cliente (rol con login). Los propietarios no traen avatar. */
  avatar?: string | null;
  avatar_url?: string | null;
}

export interface PropertyWithOwnership {
  id: number;
  nombrepropiedad: string;
  currentClientName?: string;
  currentClientId?: number;
  currentOwnerName?: string;
  currentOwnerId?: number;
}

/** Solo hay dos pestañas: cualquier otro valor guardado se descarta. */
const esTab = (v: unknown): boolean => v === "cliente" || v === "propietario";
/** Lista de textos (banco, tipo de cuenta) tal cual salen de los selects. */
const esListaDeTextos = (v: unknown): boolean =>
  Array.isArray(v) && v.every((x) => typeof x === "string");
/** Lista de ids de propiedad. */
const esListaDeIds = (v: unknown): boolean =>
  Array.isArray(v) && v.every((x) => typeof x === "number" && Number.isFinite(x));

export const useClientesLogic = () => {
  // Los filtros se recuerdan por usuario admin en este navegador
  // (round 2026-09-24, ver docs/filtros-persistentes.md).
  const [activeTab, setActiveTab] = useFiltroPersistido<TabKey>(
    "clientes.pestana",
    "cliente",
    { validar: esTab },
  );

  const [clients, setClients] = useState<ContactUser[]>([]);
  const [owners, setOwners] = useState<ContactUser[]>([]);
  const [allProperties, setAllProperties] = useState<any[]>([]);
  const [propertyFilter, setPropertyFilter] = useFiltroPersistido<number[]>(
    "clientes.propiedades",
    [],
    { validar: esListaDeIds },
  );
  // Filtros por datos de pago. Aunque banco / tipo de cuenta viven en la fila
  // expandible (no son columnas), estos selects permiten acotar la lista por
  // esos atributos. Se combinan en AND con la búsqueda y el filtro de propiedad.
  const [bancoFilter, setBancoFilter] = useFiltroPersistido<string[]>(
    "clientes.banco",
    [],
    { validar: esListaDeTextos },
  );
  const [tipocuentaFilter, setTipocuentaFilter] = useFiltroPersistido<string[]>(
    "clientes.tipoCuenta",
    [],
    { validar: esListaDeTextos },
  );
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(
    null
  );

  const { sidebarOpen, toggleSidebar } = useSidebar();
  const [loading, setLoading] = useState<boolean>(true);

  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [editingRecord, setEditingRecord] = useState<ContactUser | null>(null);
  // `submitting` cubre toda la operación de guardado (validación → upsert →
  // refetch). El backend hashea la contraseña + abre transacción + envía el
  // correo de bienvenida de forma SÍNCRONA (Resend vía HTTP, 2–4 s), así que
  // sin este flag el admin no tiene ninguna señal de que el proceso está en
  // curso y podría volver a hacer clic, disparando un segundo correo.
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [form] = Form.useForm();

  // Search (debounced). Se persiste el texto crudo; `searchTerm` se regenera
  // solo 250 ms después, así que guardarlo aparte solo los desincronizaría.
  const [searchInput, setSearchInput] = useFiltroPersistido<string>(
    "clientes.busqueda",
    "",
    { validar: (v) => typeof v === "string" },
  );
  // Arranca ya derivado del texto restaurado: si esperara al debounce, la
  // tabla se vería un cuarto de segundo sin filtrar y después saltaría.
  const [searchTerm, setSearchTerm] = useState<string>(() =>
    searchInput.trim().toLowerCase(),
  );
  const debounceRef = useRef<number | null>(null);
  const initialPropertyIdsRef = useRef<number[]>([]);
  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      setSearchTerm(searchInput.trim().toLowerCase());
    }, 250);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  useEffect(() => {
    fetchAll();
    fetchProperties();
  }, []);

  useEffect(() => {
    if (!modalVisible) return;

    form.resetFields();

    if (!editingRecord) return;

    const { propiedades_maneja, propiedades_tiene, ...rest } = editingRecord;

    const propertyIds =
      activeTab === "cliente"
        ? (propiedades_maneja || []).map((p: any) => p.id)
        : (propiedades_tiene || []).map((p: any) => p.id);

    initialPropertyIdsRef.current = propertyIds;

    form.setFieldsValue({
      ...rest,
      property_ids: propertyIds,
      password: "",
    });
  }, [modalVisible, editingRecord, activeTab, form]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const json = await apiFetch<{ clientes: any; propietarios: any }>(
        "/users/clientes-propietarios",
      );
      if (!json) return [];
      setClients(json.clientes);
      setOwners(json.propietarios);
    } finally {
      setLoading(false);
    }
  };

  const fetchProperties = async () => {
    // limit=400 (máximo del backend): esta lista alimenta el filtro por
    // propiedad — un default más bajo truncaba en silencio.
    const json = await apiFetch<{ data: any[] }>("/propiedades?limit=400");
    // El endpoint retorna { success, data }
    setAllProperties(json?.data || []);
  };

  const showModal = (record?: ContactUser) => {
    if (record) {
      setEditingRecord(record);
      getMsg()?.info(
        `Editando ${activeTab === "cliente" ? "cliente" : "propietario"}`
      );
    } else {
      setEditingRecord(null);
      form.resetFields();
    }
    setModalVisible(true);
  };

  const handleCancel = () => {
    setModalVisible(false);
    form.resetFields();
  };

  const handleSubmit = async () => {
    // La validación se hace fuera del bloque "submitting": si falla, AntD
    // ya muestra los errores inline bajo cada campo y no queremos encender
    // el spinner ni dejar el modal bloqueado por un error de formulario.
    let values: any;
    try {
      values = await form.validateFields();
    } catch (errorValidacion) {
      // El error de cada campo ya quedó marcado; falta llevar a la persona hasta el primero (el modal es más alto que la pantalla
      // de un celular y, sin esto, «Guardar» no hacía NADA visible).
      irAlPrimerError(form, errorValidacion);
      return;
    }

    // Teléfonos: `null` EXPLÍCITO para vaciar, el canónico si hay número, y OMITIDO si no cambió respecto del registro que se está
    // editando (así no se pisa lo que otra persona haya corregido, ni se reenvía un dato antiguo sin código que nadie tocó). Solo
    // los campos que este formulario tiene montados (según la pestaña): un campo ausente no se toca.
    // `telefonosEnviados`: los que de verdad viajan (los omitidos no pueden ser el rechazado). Sirve para marcar el campo cuando el
    // servidor rechaza el teléfono sin decir cuál de los tres fue (ronda F3, H7).
    const camposMontados = CAMPOS_TELEFONO.filter((c) => c in values);
    const telefonosEnviados: string[] = [];
    for (const campo of CAMPOS_TELEFONO) {
      if (!(campo in values)) continue;
      const enviar = telefonoParaEnviar(values[campo], editingRecord ? (editingRecord[campo] ?? null) : undefined);
      if (enviar === undefined) delete values[campo];
      else {
        values[campo] = enviar;
        // Un `null` (teléfono vaciado, o sin escribir al crear) tampoco puede ser el que el servidor rechazó: no es un candidato.
        if (enviar !== null) telefonosEnviados.push(campo);
      }
    }
    // Un 409 TELEFONO_DUPLICADO o un 400 TELEFONO_INVALIDO se marca además junto al campo, con el foco y el desplazamiento (antes solo
    // salía el aviso global de unos segundos y la persona no sabía cuál de los tres teléfonos corregir). El servidor NO dice cuál
    // campo falló: con un solo candidato (un único teléfono viajaba) no hay duda; con varios NO se adivina (ronda F3b: marcar solo el
    // primero dejaba el motivo bajo un número correcto y al malo sin señal) y se marcan todos con una frase que lo explica. Si
    // ninguno viajaba, los candidatos son los que el formulario tiene montado.
    const alFallarTelefono = (error: ErrorApi) => {
      marcarCamposTelefono(form, telefonosEnviados.length > 0 ? telefonosEnviados : camposMontados, error);
    };

    setSubmitting(true);
    try {
      const property_ids = Array.isArray(values.property_ids)
        ? values.property_ids
        : [];
      delete values.property_ids;

      // Política Opción B (ver docs/auth-roles.md):
      // - password vacío en CREACIÓN → backend autogenera + envía correo.
      // - password con valor en CREACIÓN → backend usa esa + envía correo.
      // - password vacío en EDICIÓN → backend deja la actual.
      // - password con valor en EDICIÓN → backend la reemplaza (sin correo).
      const adminTypedPassword =
        typeof values.password === "string" && values.password.trim() !== "";
      if (!adminTypedPassword) delete values.password;

      // rol a array si corresponde
      if (typeof values.rol === "string") values.rol = [values.rol];

      const json = await apiFetch<any>("/users/upsert", {
        method: "POST",
        body: JSON.stringify({
          id: editingRecord?.id, // si no hay, crea
          tipo: activeTab, // "cliente" | "propietario"
          user: values, // idealmente snake_case igual que DB
          property_ids, // siempre lo mandas; backend decide si cambió
        }),
        alFallar: alFallarTelefono,
      });
      if (!json) return;

      // Sólo mostramos el modal "contraseña generada" cuando el backend
      // devolvió una generatedPassword (significa que la autogeneró,
      // porque el admin no tipeó ninguna). Si el admin la tipeó, el
      // backend NO la devuelve (ya la conoce) y mostramos un toast en
      // su lugar.
      const shouldShowPassword =
        !editingRecord &&
        activeTab === "cliente" &&
        typeof json?.generatedPassword === "string" &&
        json.generatedPassword.trim() !== "";

      if (editingRecord) {
        getMsg()?.success(
          adminTypedPassword
            ? "Actualizado correctamente. La contraseña fue reemplazada."
            : "Actualizado correctamente."
        );
      } else if (activeTab === "cliente") {
        getMsg()?.success(
          adminTypedPassword
            ? "Cliente creado. Se envió un correo de bienvenida con la contraseña que ingresaste."
            : "Cliente creado. Se generó una contraseña temporal y se envió por correo."
        );
      } else {
        getMsg()?.success("Propietario creado correctamente.");
      }

      setModalVisible(false);

      if (shouldShowPassword) {
        setGeneratedPassword(json.generatedPassword);
      }

      await fetchAll();
    } catch (error: any) {
      console.error(error);
      getMsg()?.error(error?.message || "Error al guardar.");
    } finally {
      // Pase lo que pase (éxito, error de red o de unicidad) liberamos el
      // botón para que el admin pueda reintentar o cerrar el modal.
      setSubmitting(false);
    }
  };

  // --- Borrado en dos pasos ---
  const [deleteCheck, setDeleteCheck] = useState<{
    userId: number;
    nombre: string;
    propiedades: { id: number; nombre: string; rol: string }[];
  } | null>(null);

  const handleDeleteRequest = async (id: number) => {
    try {
      const res = await api.usersApi.checkDelete(id);
      if (!res) return;
      setDeleteCheck({
        userId: res.userId,
        nombre: res.nombre,
        propiedades: res.propiedadesAsociadas ?? [],
      });
    } catch (error) {
      console.error("Error al verificar:", error);
      getMsg()?.error("Error al verificar usuario.");
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteCheck) return;
    try {
      await api.usersApi.deleteUser(deleteCheck.userId);
      getMsg()?.success("Eliminado correctamente.");
      setDeleteCheck(null);
      fetchAll();
    } catch (error) {
      console.error("Error al eliminar:", error);
      getMsg()?.error("Error al eliminar.");
    }
  };

  const handleDeleteCancel = () => setDeleteCheck(null);

  /** All properties annotated with their current client/owner assignment */
  const propertiesWithOwnership: PropertyWithOwnership[] = useMemo(() => {
    return (allProperties || []).map((p: any) => {
      const prop: PropertyWithOwnership = {
        id: p.id,
        nombrepropiedad: p.nombrepropiedad,
      };

      // Find client that manages this property
      for (const c of clients || []) {
        const manages = (c as any).propiedades_maneja || [];
        if (manages.some((m: any) => m.id === p.id)) {
          prop.currentClientName = (c as any).nombreUsuario || "Sin nombre";
          prop.currentClientId = c.id;
          break;
        }
      }

      // Find owner that owns this property
      for (const o of owners || []) {
        const owns = (o as any).propiedades_tiene || [];
        if (owns.some((t: any) => t.id === p.id)) {
          prop.currentOwnerName = (o as any).nombrePropietario || "Sin nombre";
          prop.currentOwnerId = o.id;
          break;
        }
      }

      return prop;
    });
  }, [allProperties, clients, owners]);

  /** Unique properties from users for the toolbar filter */
  const filterPropertyOptions = useMemo(() => {
    const propMap = new Map<number, string>();
    const addProps = (list: ContactUser[], field: string) => {
      for (const u of list || []) {
        for (const p of (u as any)[field] || []) {
          if (p.id && p.nombrepropiedad) propMap.set(p.id, p.nombrepropiedad);
        }
      }
    };
    addProps(clients, "propiedades_maneja");
    addProps(owners, "propiedades_tiene");
    // Also include all properties from the master list
    for (const p of allProperties || []) {
      if (p.id && p.nombrepropiedad) propMap.set(p.id, p.nombrepropiedad);
    }
    return Array.from(propMap.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([id, name]) => ({ value: id, label: formatPropertyName(name) }));
  }, [clients, owners, allProperties]);

  /** Bancos presentes en los datos (cliente u propietario) para el filtro.
   *  Derivado de los datos reales: solo aparecen bancos que alguien usa. */
  const bancoFilterOptions = useMemo(() => {
    const set = new Set<string>();
    for (const u of [...(clients || []), ...(owners || [])]) {
      const v = (u as any).bancoQuienRecibe;
      if (v && String(v).trim()) set.add(String(v).trim());
    }
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b, "es", { sensitivity: "base" }))
      .map((v) => ({ value: v, label: v }));
  }, [clients, owners]);

  /** Tipos de cuenta presentes en los datos para el filtro. */
  const tipocuentaFilterOptions = useMemo(() => {
    const set = new Set<string>();
    for (const u of [...(clients || []), ...(owners || [])]) {
      const v = (u as any).tipocuentaQuienRecibe;
      if (v && String(v).trim()) set.add(String(v).trim());
    }
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b, "es", { sensitivity: "base" }))
      .map((v) => ({ value: v, label: v }));
  }, [clients, owners]);

  // Búsqueda extendida: además de nombre/email, hace match contra los datos de
  // pago (banco, tipo y N° de cuenta, RUT y datos del receptor) aunque estén
  // colapsados en la fila expandible, para que el buscador "los encuentre".
  //
  // matchesDigits: comparación solo-dígitos para teléfono/RUT — la tabla
  // muestra el teléfono agrupado con espacios (formatPhoneDisplay) y el RUT
  // con puntos/guión, pero la DB los guarda pegados / el usuario los tipea
  // sin puntos. Sin esto, copiar el dato visible no matcheaba (aud. 2026-07-13).
  const digitsOf = (v: any) => String(v ?? "").replace(/\D/g, "");
  const matchesDigits = (rawTerm: string, ...fields: any[]) => {
    const qd = rawTerm.replace(/\D/g, "");
    if (!qd) return false;
    return fields.some((f) => digitsOf(f).includes(qd));
  };

  const filteredClients = useMemo(() => {
    const t = normalize(searchTerm);
    return (clients || []).filter((u: any) => {
      if (
        !matches(
          t,
          u.nombreUsuario,
          u.emailUsuario,
          u.telefonoUsuario,
          u.rutQuienRecibe,
          u.bancoQuienRecibe,
          u.tipocuentaQuienRecibe,
          u.numerocuentaQuienRecibe,
          u.nombreQuienRecibe,
          u.emailQuienRecibe,
          u.telefonoQuienRecibe,
        ) &&
        !matchesDigits(
          searchTerm,
          u.telefonoUsuario,
          u.telefonoQuienRecibe,
          u.rutQuienRecibe,
        )
      )
        return false;
      if (propertyFilter.length > 0) {
        const userPropIds = (u.propiedades_maneja || []).map((p: any) => p.id);
        if (!propertyFilter.some((id) => userPropIds.includes(id))) return false;
      }
      if (bancoFilter.length > 0 && !bancoFilter.includes(u.bancoQuienRecibe))
        return false;
      if (
        tipocuentaFilter.length > 0 &&
        !tipocuentaFilter.includes(u.tipocuentaQuienRecibe)
      )
        return false;
      return true;
    });
  }, [clients, searchTerm, propertyFilter, bancoFilter, tipocuentaFilter]);

  const filteredOwners = useMemo(() => {
    const t = normalize(searchTerm);
    return (owners || []).filter((u: any) => {
      if (
        !matches(
          t,
          u.nombrePropietario,
          u.emailPropietario,
          u.telefonoPropietario,
          u.rutPropietario,
          u.rutQuienRecibe,
          u.bancoQuienRecibe,
          u.tipocuentaQuienRecibe,
          u.numerocuentaQuienRecibe,
          u.nombreQuienRecibe,
          u.emailQuienRecibe,
          u.telefonoQuienRecibe,
        ) &&
        !matchesDigits(
          searchTerm,
          u.telefonoPropietario,
          u.telefonoQuienRecibe,
          u.rutPropietario,
          u.rutQuienRecibe,
        )
      )
        return false;
      if (propertyFilter.length > 0) {
        const userPropIds = (u.propiedades_tiene || []).map((p: any) => p.id);
        if (!propertyFilter.some((id) => userPropIds.includes(id))) return false;
      }
      if (bancoFilter.length > 0 && !bancoFilter.includes(u.bancoQuienRecibe))
        return false;
      if (
        tipocuentaFilter.length > 0 &&
        !tipocuentaFilter.includes(u.tipocuentaQuienRecibe)
      )
        return false;
      return true;
    });
  }, [owners, searchTerm, propertyFilter, bancoFilter, tipocuentaFilter]);

  return {
    activeTab,
    setActiveTab,
    generatedPassword,
    setGeneratedPassword,

    clients: filteredClients,
    owners: filteredOwners,
    allProperties,
    propertiesWithOwnership,
    filterPropertyOptions,
    propertyFilter,
    setPropertyFilter,
    bancoFilter,
    setBancoFilter,
    tipocuentaFilter,
    setTipocuentaFilter,
    bancoFilterOptions,
    tipocuentaFilterOptions,
    loading,
    submitting,
    sidebarOpen,
    modalVisible,
    editingRecord,
    form,

    searchInput,
    setSearchInput,

    toggleSidebar,
    showModal,
    handleCancel,
    handleSubmit,
    deleteCheck,
    handleDeleteRequest,
    handleDeleteConfirm,
    handleDeleteCancel,
  };
};
